import {useQuery} from "@apollo/client";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import toast from "react-hot-toast";

import {Alert} from "@/components/alert/Alert.tsx";
import {Card} from "@/components/card/Card.tsx";
import {useDateRange} from "@/components/datePicker/DatePickerURLRangeParamsContext.ts";
import {Last24Hours} from "@/components/datePicker/subComponents/DatePickerShortcutList/DatePickerShortcutList.utils.ts";
import {Input} from "@/components/input/Input.tsx";
import {ActionDialog} from "@/components/overlay/ActionDialog.tsx";
import {LoadingContainer} from "@/components/placeholder/LoadingContainer.tsx";
import {
  GQL_METRICS_EXPLORER_DOWNTIME,
  GQL_METRICS_EXPLORER_QUERY,
  GQL_METRICS_EXPLORER_SCHEMA,
  MetricsExplorerDowntimeData,
  MetricsExplorerQueryData,
  MetricsExplorerSchemaData,
} from "@/lib/queries/metricsExplorer.ts";
import {CheckInfo} from "@/lib/queries/statuspages.ts";
import {MetricsExplorerView} from "@/lib/types/metricsExplorer.ts";
import {AnalysisControls} from "@/pages/metricsExplorer/analysis/AnalysisControls.tsx";
import {AnalysisReadout} from "@/pages/metricsExplorer/analysis/AnalysisReadout.tsx";
import {applyCatalogDefaults} from "@/pages/metricsExplorer/catalogDefaults.ts";
import {buildDowntimeWindows} from "@/pages/metricsExplorer/chartShapes.ts";
import {singleCheckId, toAnalysisInput, toQuerySpecs} from "@/pages/metricsExplorer/explorerConfig.ts";
import {ExportChartButton} from "@/pages/metricsExplorer/ExportChartButton.tsx";
import {MetricsExplorerChart} from "@/pages/metricsExplorer/MetricsExplorerChart.tsx";
import {MetricsExplorerToolbar} from "@/pages/metricsExplorer/MetricsExplorerToolbar.tsx";
import {SavedViewsBar} from "@/pages/metricsExplorer/savedViews/SavedViewsBar.tsx";
import {useSavedViews} from "@/pages/metricsExplorer/savedViews/useSavedViews.ts";
import {SeriesBuilderRail} from "@/pages/metricsExplorer/seriesBuilder/SeriesBuilderRail.tsx";
import {useExplorerConfig} from "@/pages/metricsExplorer/useExplorerConfig.ts";

import styles from "./MetricsExplorerContent.module.scss";

export const MetricsExplorerContent = () => {
  const {
    config,
    setConfig,
    requestedViewId,
    adoptRequestedView,
    dismissRequestedView,
    updateSeries,
    addSeries,
    removeSeries,
    duplicateSeries,
  } = useExplorerConfig();
  const {dateRange, fixedRangeOption, handleRangeChange} = useDateRange({
    fromKey: "start",
    toKey: "end",
    fixedRangeKey: "range",
    withDefault: Last24Hours,
  });

  // Check names and types are needed to label series and to narrow the catalog, but the
  // config only stores ids. The pickers hand back the full records as they resolve them.
  const [checkMeta, setCheckMeta] = useState<Record<number, CheckInfo>>({});
  const handleChecksResolved = useCallback((checks: CheckInfo[]) => {
    setCheckMeta((previous) => {
      const next = {...previous};
      checks.forEach((check) => {
        next[check.id] = check;
      });
      return next;
    });
  }, []);

  const {data: schemaData} = useQuery<MetricsExplorerSchemaData>(GQL_METRICS_EXPLORER_SCHEMA, {
    // The catalog only changes when metric_defs.yaml is deployed.
    fetchPolicy: "cache-first",
  });

  // Picking a check should draw something straight away rather than leaving two more
  // dropdowns to work through, and a preset link deliberately arrives without them.
  const schema = schemaData?.metricsExplorerSchema;
  useEffect(() => {
    const filled = applyCatalogDefaults(config, schema, checkMeta);
    if (filled !== config) {
      setConfig(filled);
    }
  }, [config, schema, checkMeta, setConfig]);

  const specs = useMemo(() => toQuerySpecs(config), [config]);

  const chartRef = useRef<HTMLDivElement | null>(null);
  const queryInput = useMemo(
    () =>
      specs.length === 0
        ? null
        : {
            start_date: dateRange.from.toISOString(),
            end_date: dateRange.to.toISOString(),
            granularity: config.granularity,
            series: specs,
            comparison: config.comparison ? {period: config.comparison} : null,
            analysis: toAnalysisInput(config.analysis),
          },
    [specs, dateRange.from, dateRange.to, config.granularity, config.comparison, config.analysis]
  );

  const {data, loading, error} = useQuery<MetricsExplorerQueryData>(GQL_METRICS_EXPLORER_QUERY, {
    skip: queryInput === null,
    variables: {query: queryInput},
  });

  // Downtime belongs to one check, so the overlay is only offered when the chart is about
  // one; a band on a multi-check chart would not say which series it applied to.
  const downtimeCheckId = singleCheckId(config);
  const {data: downtimeData} = useQuery<MetricsExplorerDowntimeData>(GQL_METRICS_EXPLORER_DOWNTIME, {
    skip: !config.showDowntime || downtimeCheckId === null,
    variables: {
      id: downtimeCheckId,
      start: dateRange.from.toISOString(),
      end: dateRange.to.toISOString(),
    },
  });
  const downtime = useMemo(
    () => buildDowntimeWindows(downtimeData?.stats.check_state_events ?? [], dateRange.to),
    [downtimeData, dateRange.to]
  );

  const {views, loading: viewsLoading, saveView, deleteView, configOf} = useSavedViews(true);
  const [activeView, setActiveView] = useState<MetricsExplorerView | null>(null);
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [draftName, setDraftName] = useState("");

  const openView = useCallback(
    (view: MetricsExplorerView) => {
      const saved = configOf(view);
      if (!saved) {
        toast.error(`"${view.name}" was saved in a format this page no longer understands.`);
        return;
      }
      setActiveView(view);
      setConfig(saved);
    },
    [configOf, setConfig]
  );

  // A dashboard widget links here by saved-chart id. Resolve it once the list arrives and
  // swap it for a real config in the URL, so from that point the page is indistinguishable
  // from any other shared explorer link. Waiting on `viewsLoading` is what separates "no
  // charts yet" from "not known yet" — without it an empty first render looks like a
  // missing chart.
  useEffect(() => {
    if (requestedViewId === null || viewsLoading) {
      return;
    }
    const requested = views.find((item) => item.id === requestedViewId);
    if (!requested) {
      toast.error("That chart is no longer available.");
      dismissRequestedView();
      return;
    }
    const saved = configOf(requested);
    if (!saved) {
      toast.error(`"${requested.name}" was saved in a format this page no longer understands.`);
      dismissRequestedView();
      return;
    }
    setActiveView(requested);
    adoptRequestedView(saved);
  }, [requestedViewId, viewsLoading, views, configOf, adoptRequestedView, dismissRequestedView]);

  const commitSave = useCallback(
    async (name: string, id?: number) => {
      const saved = await saveView(name, config, id);
      if (saved) {
        setActiveView(saved);
      }
    },
    [saveView, config]
  );

  const result = data?.metricsExplorerQuery;
  const checkNames = useMemo(
    () => Object.fromEntries(Object.values(checkMeta).map((check) => [check.id, check.name])) as Record<number, string>,
    [checkMeta]
  );

  return (
    <div className={styles.layout}>
      <SavedViewsBar
        views={views}
        activeView={activeView}
        canSave={specs.length > 0}
        configOf={configOf}
        onOpen={openView}
        onSave={() => {
          if (activeView) {
            commitSave(activeView.name, activeView.id);
            return;
          }
          setDraftName("");
          setNameDialogOpen(true);
        }}
        onSaveAs={() => {
          setDraftName(`${activeView?.name ?? "Chart"} copy`);
          setNameDialogOpen(true);
        }}
        onDelete={async () => {
          if (activeView && (await deleteView(activeView.id))) {
            setActiveView(null);
          }
        }}
      />

      <MetricsExplorerToolbar
        start={dateRange.from}
        end={dateRange.to}
        fixedRange={fixedRangeOption?.value}
        granularity={config.granularity}
        comparison={config.comparison}
        chartType={config.chartType}
        onGranularityChange={(granularity) => setConfig({...config, granularity})}
        onComparisonChange={(comparison) => setConfig({...config, comparison})}
        onChartTypeChange={(chartType) => setConfig({...config, chartType})}
        onRangeChange={(start, end, fixedRange) =>
          handleRangeChange(start && end ? {from: start, to: end} : null, fixedRange)
        }
      >
        <ExportChartButton query={queryInput} checkNames={checkNames} chartRef={chartRef} />
      </MetricsExplorerToolbar>

      <div className={styles.body}>
        <div className={styles.main}>
          <Card variant="white" className={styles.chartPanel}>
            {error && <Alert variant="error">{error.message}</Alert>}
            {result?.errors.map((seriesError) => (
              <Alert key={`${seriesError.ref}-${seriesError.code}`} variant="default">
                {seriesError.message}
              </Alert>
            ))}
            {loading && !result ? (
              <LoadingContainer text="Querying metrics…" />
            ) : (
              <div ref={chartRef}>
                <MetricsExplorerChart
                  series={result?.series ?? []}
                  analysis={result?.analysis ?? []}
                  chartType={config.chartType}
                  checkNames={checkNames}
                  granularity={result?.meta.granularity ?? config.granularity}
                  start={dateRange.from}
                  end={dateRange.to}
                  downtime={downtime}
                />
              </div>
            )}
            {result && (
              <div className={styles.meta}>
                <span>{result.meta.total_datapoints} points</span>
                <span>{result.meta.query_count} queries</span>
                <span>{result.meta.query_time_ms} ms</span>
                {result.meta.truncated && <span>Truncated — narrow the range or the series.</span>}
              </div>
            )}
          </Card>

          {result && result.analysis.length > 0 && (
            <AnalysisReadout analysis={result.analysis} series={result.series} />
          )}
        </div>

        <div className={styles.rail}>
          <SeriesBuilderRail
            config={config}
            schema={schema}
            checkMeta={checkMeta}
            start={dateRange.from}
            end={dateRange.to}
            onUpdateSeries={updateSeries}
            onAddSeries={addSeries}
            onRemoveSeries={removeSeries}
            onDuplicateSeries={duplicateSeries}
            onChecksResolved={handleChecksResolved}
          />
          <AnalysisControls
            analysis={config.analysis}
            showDowntime={config.showDowntime}
            downtimeAvailable={downtimeCheckId !== null}
            onChange={(patch) => setConfig({...config, analysis: {...config.analysis, ...patch}})}
            onShowDowntimeChange={(showDowntime) => setConfig({...config, showDowntime})}
          />
        </div>
      </div>

      <ActionDialog
        show={nameDialogOpen}
        title="Name this chart"
        confirmBtnText="Save"
        disableConfirmButton={draftName.trim() === ""}
        onConfirm={() => {
          setNameDialogOpen(false);
          commitSave(draftName.trim());
        }}
        onCancel={() => setNameDialogOpen(false)}
      >
        <Input
          size="small"
          value={draftName}
          placeholder="Response time by step"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDraftName(event.currentTarget.value)}
        />
      </ActionDialog>
    </div>
  );
};
